import * as Api from '@ViewModels';
import * as React from 'react';
import { SalesLeadAIContext } from '../../../extViewmodels/sdk/models/AI';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useAIConversationEnrichCreationContextMutation } from '../../../queries/AI/useAIConversationEnrichCreationContextMutation';
import { useAIConversationChatMessageQuery } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { COACH_ASSISTANT_NAME, LIVE_SUGGESTION_PROMPT_HEADER } from './prompts';

/**
 * Local mirror of the upstream `AICoachingMessage` shape. We don't import that
 * type so the focused view's AI layer doesn't depend on `voice-agent/`. The
 * shape is dictated by the SSE stream payload format (which is server-side),
 * not by the upstream hook.
 */
export interface CoachUpstreamMessage {
	id: string;
	text: string;
	timestamp: Date;
	isStreaming: boolean;
	role: 'user' | 'assistant';
	messageType?: 'transcript' | 'question' | 'system';
}

export interface UseRealtimeCoachingArgs {
	companyId?: string;
	leadServedId?: string;
	phoneCallId?: string;
	/**
	 * Optional AI agent template id. When set, it is forwarded to the
	 * conversation-create endpoint so the server selects the matching system
	 * prompt / behavior. Mirrors `useTysonRealtimeCoaching`'s parameter of the
	 * same name. Only applied on a fresh `createNew`; resumed conversations
	 * already carry the template they were created with.
	 */
	agentTemplateId?: string;
}

/**
 * Options for the hook itself (vs. options for a specific `createNew` call).
 * `companyId` here is used to attach the `SalesLeadAIContext` to every
 * outbound stream so the AI has lead-scoped data on the server side — even
 * for sends that happen after a resume.
 */
export interface UseRealtimeCoachingOptions {
	companyId?: string;
}

export interface UseRealtimeCoachingApi {
	coachingMessages: CoachUpstreamMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isLoadingHistory: boolean;
	isAIResponding: boolean;
	/** True if the active conversation was resumed from the cache (vs newly created). */
	wasResumed: boolean;
	/**
	 * Create a new server-side conversation. The starter prompt is injected
	 * server-side on the first user-message stream (via `autoStart: true`), not
	 * at creation time, so resumed conversations don't get a redundant prompt.
	 */
	createNew(args: UseRealtimeCoachingArgs): Promise<string | null>;
	/** Adopt an existing conversation and load its message history. */
	resume(conversationId: string): void;
	sendDirectQuestion(question: string): void;
	sendSystemMessage(message: string): void;
	/**
	 * Append a finalized transcript entry to the local message stream so the UI
	 * can render it and `endCall` can detect that real transcription happened.
	 * Does NOT auto-stream the entry to the AI — the caller decides when to
	 * batch transcript content (typically via the end-of-call summary).
	 */
	addTranscriptEntry(entry: { id: string; speaker: string; text: string; timestamp: Date }): void;
	/**
	 * Format and send any transcript entries accumulated since the last call
	 * (or since `addTranscriptEntry` started). Returns the number of entries
	 * flushed; callers can use this to release a queue lock if no transcript
	 * was actually pending.
	 */
	sendTranscriptSuggestion(): number;
	/**
	 * Fire the end-of-call summary action, attaching any transcript entries that
	 * were never pushed upstream (the whole call when auto-suggestions are off,
	 * the tail after the last suggestion when they're on). Without that attach
	 * the BE would summarize a conversation whose only content is the Game Plan —
	 * `addTranscriptEntry` keeps entries client-side by design.
	 */
	requestPostCallSummary(): void;
	enrichCreationContext(params: { phoneCallId?: string; leadServedId?: string; source?: string }): void;
	reset(): void;
}

const nowId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatTranscriptEntries = (entries: Array<{ speaker: string; text: string; timestamp: Date }>): string =>
	entries.map(e => `[${e.timestamp.toLocaleTimeString()}] ${e.speaker}: ${e.text}`).join('\n');

/**
 * Map a persisted `AIChatMessage` (from the chat-message GET) into the local
 * `CoachUpstreamMessage` shape used by the focused view. Persisted messages
 * are never streaming. `messageType` is left unset because the persisted
 * record doesn't carry it; consumers that need to distinguish transcript vs
 * direct-question entries must rely on text-content heuristics or wait for
 * a server-side schema change.
 */
function fromPersistedMessage(m: Api.AIChatMessage): CoachUpstreamMessage {
	return {
		id: m.id || nowId('persisted'),
		text: m.text ?? '',
		timestamp: new Date(),
		isStreaming: false,
		role: m.role === Api.AIChatRole.Assistant ? 'assistant' : 'user',
	};
}

/**
 * Focused-view-owned realtime coaching hook. Replaces the upstream
 * `useTysonRealtimeCoaching` + `TysonCoachingContext` pair for callers under
 * `focused-view/`. Reuses only the underlying HTTP adapters
 * (`useAIAssistantCreateConversationMutation`,
 * `useAIConversationEnrichCreationContextMutation`,
 * `useAIConversationChatMessageQuery`, `useStreamingResponseMutation`); all
 * conversation state, message accumulation, and lifecycle is owned here.
 *
 * Two entry points:
 *   - `createNew(args)` POSTs to `ai/assistant/{name}/conversation` and
 *     returns the new id. Does **not** auto-fire a bootstrap message — instead
 *     it arms `pendingAutoStartRef` so the first outbound stream carries
 *     `autoStart: true` and the server-side agent injects its starter prompt
 *     (typically triggered by the first `before_call` keyword pin).
 *   - `resume(id)` adopts an existing id and loads the persisted message
 *     history via the chat-message query. AutoStart is suppressed because the
 *     starter prompt is already in the conversation history.
 *
 * Outbound user messages stream through `aiPromptMutation`; server-handled
 * actions (e.g. the post-call summary) go through the same endpoint as an
 * `AIActionRequest`. The SSE `Response` events accumulate into
 * `coachingMessages`. Streamed tokens are intentionally NOT mirrored into the
 * chat-message react-query cache — the server persists outbound messages, so a
 * future `resume(id)` after reload backfills the authoritative history.
 */
export function useRealtimeCoaching(opts?: UseRealtimeCoachingOptions): UseRealtimeCoachingApi {
	const userSession = useUserSession();

	const [conversationId, setConversationId] = React.useState<string | null>(null);
	const [coachingMessages, setCoachingMessages] = React.useState<CoachUpstreamMessage[]>([]);
	const [wasResumed, setWasResumed] = React.useState(false);
	const conversationIdRef = React.useRef<string | null>(null);
	// `companyId` is read by `sendStream` to attach a `SalesLeadAIContext` on
	// every outbound stream. We keep it in a ref synced via an effect so the
	// callback dependency closure stays stable while still seeing the latest
	// value across renders.
	const companyIdRef = React.useRef<string | undefined>(opts?.companyId);
	React.useEffect(() => {
		companyIdRef.current = opts?.companyId;
	}, [opts?.companyId]);
	// Tracks whether the next outbound stream should set `autoStart: true` so
	// the server-side agent injects its starter prompt. Set to `true` by
	// `createNew()`, set to `false` by `resume()` (the existing conversation
	// already has a starter prompt) and after the first stream consumes it.
	// The backend rejects `autoStart: true` once messages exist, so the ref
	// must be flipped before the second stream.
	const pendingAutoStartRef = React.useRef(false);
	// Tracks which conversationId we've already backfilled history for. The
	// historyQuery's `data` reference can change after the initial fetch (e.g.
	// react-query refetch on focus, or any other consumer mutating the cache),
	// and we must NOT re-replace local state on those subsequent changes —
	// doing so would wipe streaming and user-composed messages.
	const backfilledIdRef = React.useRef<string | null>(null);
	// Unsent transcript entries waiting for the next "ask for a suggestion"
	// dispatch. Populated by `addTranscriptEntry`, drained by
	// `sendTranscriptSuggestion`. The user-visible chat already has each entry
	// as its own `messageType: 'transcript'` message via `addTranscriptEntry`;
	// this ref only powers the AI batch send.
	const pendingTranscriptRef = React.useRef<Array<{ speaker: string; text: string; timestamp: Date }>>([]);
	// Synchronous id dedup for `addTranscriptEntry`. The transcript pipeline
	// CAN emit two `final: true` events for the same utterance (Twilio
	// `lastSentence` markers etc.) and the React state update pattern alone
	// can't distinguish a duplicate from a genuine re-render before commit.
	// A ref-tracked Set checked synchronously fixes both.
	const seenTranscriptIdsRef = React.useRef<Set<string>>(new Set());

	React.useEffect(() => {
		conversationIdRef.current = conversationId;
	}, [conversationId]);

	const createMutation = useAIAssistantCreateConversationMutation({
		onError: () => {
			// Caller observes via the returned promise / `isCreatingConversation`.
		},
	});

	const enrichMutation = useAIConversationEnrichCreationContextMutation({
		onError: () => {},
	});

	// Backfill on resume. `enabled` is gated on `wasResumed && conversationId`
	// so we don't fetch for new conversations (whose history is empty anyway).
	const historyQuery = useAIConversationChatMessageQuery({
		id: conversationId ?? '',
		enabled: wasResumed && !!conversationId,
		staleTime: Infinity,
	});

	React.useEffect(() => {
		if (!wasResumed) return;
		if (!conversationId) return;
		if (backfilledIdRef.current === conversationId) return;
		const data = historyQuery.data;
		if (!data) return;
		backfilledIdRef.current = conversationId;
		setCoachingMessages(data.map(fromPersistedMessage));
	}, [wasResumed, conversationId, historyQuery.data]);

	// SSE stream for outbound user messages and post-call summary actions.
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ convoId, chatRequest }: { convoId: string; chatRequest: Api.AIChatRequest }) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${COACH_ASSISTANT_NAME}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			if (event.event !== 'Response') return;
			// Drop events from a stream whose conversation is no longer active.
			// On a lead switch `reset()` nulls `conversationIdRef` and the
			// subsequent `createNew`/`resume` repoints it to the new lead; without
			// this guard the previous lead's still-open stream — e.g. a post-call
			// summary — keeps appending its tokens into the new lead's freshly-reset
			// panel. The `reset()` abort below races the in-flight fetch, so this
			// guard is the authoritative backstop.
			if (params.convoId !== conversationIdRef.current) return;
			let data: { message?: { text?: string }; isDelta?: boolean; Value?: { Id?: string } };
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}
			const messageText = data?.message?.text;
			const isDelta = data?.isDelta;
			if (!messageText) return;

			setCoachingMessages(prev => {
				const last = prev[prev.length - 1];
				if (last && last.isStreaming && last.role === 'assistant') {
					const updated = isDelta ? last.text + messageText : messageText;
					return [...prev.slice(0, -1), { ...last, text: updated }];
				}
				return [
					...prev,
					{
						id: data.Value?.Id || nowId('msg'),
						text: messageText,
						timestamp: new Date(),
						isStreaming: true,
						role: 'assistant',
					},
				];
			});
			// Intentionally NOT mirroring streamed tokens into the persisted
			// chat-message react-query cache. The previous implementation did
			// that as a perf optimization (warm cache for next reload), but it
			// caused the history-backfill effect to re-fire on every cache
			// update and wipe local user messages. The server persists outbound
			// messages anyway; on next resume, the chat-message GET pulls the
			// authoritative history.
		},
		onSuccess: params => {
			// Same stale-stream guard as `onEvent`: don't finalize the streaming
			// flag on the new lead's panel when a previous lead's stream resolves.
			if (params.convoId !== conversationIdRef.current) return;
			setCoachingMessages(prev => {
				if (prev.length === 0) return prev;
				const last = prev[prev.length - 1];
				if (!last.isStreaming) return prev;
				return [...prev.slice(0, -1), { ...last, isStreaming: false }];
			});
		},
	});

	// Stable handle on the streaming mutation's `reset` (which aborts the
	// in-flight fetch via its AbortController). `useStreamingResponseMutation`
	// returns a fresh object each render, so we mirror its `reset` into a ref to
	// keep our own `reset` callback dependency-free and stable.
	const abortStreamRef = React.useRef(aiPromptMutation.reset);
	abortStreamRef.current = aiPromptMutation.reset;

	/**
	 * Internal: send a stream request, attaching `autoStart: true` on the very
	 * first send for a new conversation so the server-side agent injects its
	 * starter prompt. Resumed conversations skip `autoStart` because the
	 * starter prompt is already in the persisted history.
	 */
	const sendStream = React.useCallback(
		(convoId: string, userMessages: Api.AIChatMessage[], contexts?: Api.AIContext[]) => {
			const autoStart = pendingAutoStartRef.current;
			if (autoStart) pendingAutoStartRef.current = false;

			// Attach `SalesLeadAIContext({companyId})` to every outbound stream
			// (mirrors legacy `useTysonRealtimeCoaching`). The server uses this
			// to load lead-scoped data — without it the AI has no lead context
			// even though the conversation was created with `companyId`. If the
			// caller already supplied a `SalesLeadAIContext`, we don't duplicate.
			const mergedContexts: Api.AIContext[] = [...(contexts ?? [])];
			const companyId = companyIdRef.current;
			if (companyId) {
				const hasLeadContext = mergedContexts.some(c => (c as { _type?: string })._type === 'SalesLeadAIContext');
				if (!hasLeadContext) {
					mergedContexts.push({ _type: 'SalesLeadAIContext', companyId } as SalesLeadAIContext);
				}
			}

			aiPromptMutation.mutate({
				convoId,
				chatRequest: {
					...(mergedContexts.length > 0 ? { contexts: mergedContexts } : {}),
					...(autoStart ? { autoStart: true } : {}),
					chatMessages: userMessages,
				} as Api.AIMessageRequest,
			});
		},
		[aiPromptMutation]
	);

	const createNew = React.useCallback(
		async (args: UseRealtimeCoachingArgs): Promise<string | null> => {
			if (!args.companyId) return null;
			if (createMutation.isPending) return null;
			try {
				const conversation = await createMutation.mutateAsync({
					assistantName: COACH_ASSISTANT_NAME,
					phoneCallId: args.phoneCallId,
					companyId: args.companyId,
					leadServedId: args.leadServedId,
					agentTemplateId: args.agentTemplateId,
				});
				if (!conversation?.id) return null;
				pendingAutoStartRef.current = true;
				setWasResumed(false);
				setCoachingMessages([]);
				// Update the ref synchronously (not just via the commit effect) so
				// the `onEvent` convoId guard sees the new id during the render gap
				// before the effect runs — otherwise the first tokens of the new
				// conversation's own stream could be rejected as "stale".
				conversationIdRef.current = conversation.id;
				setConversationId(conversation.id);
				return conversation.id;
			} catch {
				return null;
			}
		},
		[createMutation]
	);

	const resume = React.useCallback((id: string) => {
		pendingAutoStartRef.current = false;
		setWasResumed(true);
		setCoachingMessages([]); // history backfill effect populates from query data
		// Keep the ref in lockstep with state (see `createNew`) so the stale-
		// stream guard resolves correctly immediately after a resume.
		conversationIdRef.current = id;
		setConversationId(id);
	}, []);

	const sendDirectQuestion = React.useCallback(
		(question: string) => {
			const convoId = conversationIdRef.current;
			const trimmed = question.trim();
			if (!convoId || !trimmed) return;
			const userMessage: CoachUpstreamMessage = {
				id: nowId('question'),
				text: trimmed,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'question',
			};
			setCoachingMessages(prev => [...prev, userMessage]);
			const wrapped = `The sales rep is asking you a direct question (not from the call transcript):\n${trimmed}`;
			// `sendStream` itself attaches `SalesLeadAIContext({companyId})` (when
			// the hook was called with a `companyId`), so the AI sees lead-scoped
			// data on every send — including this one. We do not pass an
			// explicit `contexts` array here.
			sendStream(convoId, [{ role: Api.AIChatRole.User, text: wrapped, id: null }]);
		},
		[sendStream]
	);

	const sendSystemMessage = React.useCallback(
		(message: string) => {
			const convoId = conversationIdRef.current;
			const trimmed = message.trim();
			if (!convoId || !trimmed) return;
			const sysMessage: CoachUpstreamMessage = {
				id: nowId('system'),
				text: trimmed,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'system',
			};
			setCoachingMessages(prev => [...prev, sysMessage]);
			sendStream(convoId, [{ role: Api.AIChatRole.User, text: trimmed, id: null }]);
		},
		[sendStream]
	);

	const addTranscriptEntry = React.useCallback(
		(entry: { id: string; speaker: string; text: string; timestamp: Date }) => {
			const trimmed = entry.text.trim();
			if (!trimmed) return;
			// Synchronous dedup — see `seenTranscriptIdsRef` for why this isn't
			// done inside the setCoachingMessages updater.
			if (seenTranscriptIdsRef.current.has(entry.id)) return;
			seenTranscriptIdsRef.current.add(entry.id);

			const formatted = `${entry.speaker}: ${trimmed}`;
			const message: CoachUpstreamMessage = {
				id: entry.id,
				text: formatted,
				timestamp: entry.timestamp,
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};
			setCoachingMessages(prev => [...prev, message]);
			pendingTranscriptRef.current.push({
				speaker: entry.speaker,
				text: trimmed,
				timestamp: entry.timestamp,
			});
		},
		[]
	);

	const sendTranscriptSuggestion = React.useCallback((): number => {
		const convoId = conversationIdRef.current;
		if (!convoId) return 0;
		const entries = pendingTranscriptRef.current;
		if (entries.length === 0) return 0;
		pendingTranscriptRef.current = [];
		const formatted = formatTranscriptEntries(entries);
		// The "LIVE COACHING REQUEST" header is the contract documented in
		// `aida/constants/coachingContract.ts`: when the BE-side
		// coach agent sees that header, it must produce a tactical in-the-
		// moment suggestion, NOT a summary. Without this header, a previous
		// `CoachPostCallSummaryAction` earlier in the conversation history
		// can bias the model into summary mode for subsequent transcript
		// batches.
		const text = `${LIVE_SUGGESTION_PROMPT_HEADER}\n\n${formatted}`;
		// Don't append a local user message — each entry is already in the chat
		// as `messageType: 'transcript'` via `addTranscriptEntry`. We just push
		// the batch text up to the AI so it has the recent context for its
		// suggestion reply.
		sendStream(convoId, [{ role: Api.AIChatRole.User, text, id: null }]);
		return entries.length;
	}, [sendStream]);

	const requestPostCallSummary = React.useCallback(() => {
		const convoId = conversationIdRef.current;
		if (!convoId) return;
		// Drain whatever transcript never made it upstream and ride it along on the
		// action. With auto-suggestions off that's the entire call; with them on
		// it's whatever the lead/rep said after the last suggestion. Draining here
		// (dispatch time) rather than when the summary was queued means utterances
		// that land while the queue waits on an in-flight stream are included too.
		const pending = pendingTranscriptRef.current;
		pendingTranscriptRef.current = [];
		const formattedTranscript = pending.length > 0 ? formatTranscriptEntries(pending) : undefined;
		// Fire the structured action; the BE handler owns the summary
		// instruction text. The `_type: 'AIActionRequest'` discriminant is
		// REQUIRED: the BE deserializes `AIChatRequest` polymorphically and
		// falls back to `AIMessageRequest` when `_type` is absent, which then
		// 400s with "Message request must include messages".
		//
		// `autoStart` is NOT attached — the BE rejects it on a non-message
		// request ("AutoStart requires an AIMessageRequest"). But we still
		// clear any pending autoStart: sending this summary creates messages,
		// so a later stream must not try to boot a now-non-empty conversation.
		pendingAutoStartRef.current = false;
		const action = {
			_type: 'CoachPostCallSummaryAction',
			...(formattedTranscript ? { transcript: formattedTranscript } : {}),
		} as Api.CoachPostCallSummaryAction;
		aiPromptMutation.mutate({
			convoId,
			chatRequest: {
				_type: 'AIActionRequest',
				action,
			} as Api.AIActionRequest,
		});
	}, [aiPromptMutation]);

	const enrichCreationContext = React.useCallback(
		(params: { phoneCallId?: string; leadServedId?: string; source?: string }) => {
			const convoId = conversationIdRef.current;
			if (!convoId) return;
			if (!params.phoneCallId && !params.leadServedId && !params.source) return;
			enrichMutation.mutate({
				assistantName: COACH_ASSISTANT_NAME,
				conversationId: convoId,
				...params,
			});
		},
		[enrichMutation]
	);

	const reset = React.useCallback(() => {
		// Abort any in-flight stream first so the prior lead's open SSE (e.g. a
		// post-call summary) stops emitting. The `onEvent` convoId guard catches
		// any tokens that race past the abort. Note the abort rejects the stream
		// into the streaming mutation's `onError` (currently a no-op) — if an
		// `onError` handler is ever added there, it must apply the same stale-
		// convoId guard so an aborted prior-lead stream can't surface on the new
		// lead's panel.
		abortStreamRef.current();
		setConversationId(null);
		conversationIdRef.current = null;
		setCoachingMessages([]);
		setWasResumed(false);
		pendingAutoStartRef.current = false;
		backfilledIdRef.current = null;
		pendingTranscriptRef.current = [];
		seenTranscriptIdsRef.current = new Set();
	}, []);

	return {
		coachingMessages,
		conversationId,
		isCreatingConversation: createMutation.isPending,
		isLoadingHistory: wasResumed && historyQuery.isLoading,
		isAIResponding: aiPromptMutation.isStreaming || aiPromptMutation.isLoading,
		wasResumed,
		createNew,
		resume,
		sendDirectQuestion,
		sendSystemMessage,
		addTranscriptEntry,
		sendTranscriptSuggestion,
		requestPostCallSummary,
		enrichCreationContext,
		reset,
	};
}
